import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import {
  applyTheme,
  readStoredTheme,
  resolveTheme,
  storeTheme,
  systemTheme,
  type ResolvedTheme,
  type ThemeChoice,
} from './theme';

interface ThemeContextValue {
  /** What the user picked: light, dark, or follow the system. */
  choice: ThemeChoice;
  /** What is actually painted right now. */
  resolved: ResolvedTheme;
  setChoice: (choice: ThemeChoice) => void;
  /** Cycles light → dark → system, for a single-button switcher. */
  cycle: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [choice, setChoiceState] = useState<ThemeChoice>(() => {
    // The inline head script has already applied the stored value, so reading
    // it here keeps React's state consistent with what is on screen.
    if (typeof document === 'undefined') return 'system';
    return readStoredTheme();
  });
  const [resolved, setResolved] = useState<ResolvedTheme>(() => resolveTheme(readStoredTheme()));

  const setChoice = useCallback((next: ThemeChoice) => {
    setChoiceState(next);
    storeTheme(next);
    setResolved(applyTheme(next));
  }, []);

  const cycle = useCallback(() => {
    setChoiceState((current) => {
      const next: ThemeChoice =
        current === 'light' ? 'dark' : current === 'dark' ? 'system' : 'light';
      storeTheme(next);
      setResolved(applyTheme(next));
      return next;
    });
  }, []);

  // Follow the OS while the user is on `system`, and react to the OS changing
  // under them without a reload.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;

    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => {
      if (choice === 'system') setResolved(applyTheme('system'));
    };

    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, [choice]);

  // Another tab changing the theme should be reflected here too.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onStorage = (event: StorageEvent) => {
      if (event.key !== 'dhdtech:theme') return;
      const next = readStoredTheme();
      setChoiceState(next);
      setResolved(applyTheme(next));
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ choice, resolved, setChoice, cycle }),
    [choice, resolved, setChoice, cycle],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used inside a <ThemeProvider>');
  return context;
}

/** Reads the live system preference without subscribing. */
export { systemTheme };
